import { useList } from '@refinedev/core';
import { useMutation } from '@tanstack/react-query';
import { useCallback, useEffect, useRef } from 'react';

import { FAIL_REPORT_URLS } from '@/constants/urls';
import { FailReportItem } from '@/model/FailReport/FailReportItem';
import { queryKeys } from '@/repositories/queryKeys';
import { useRepositoriesContext } from '@/repositories/RepositoriesContext';

const useFailReportItems = (
  failReportId,
  failReportItems: FailReportItem[],
  setFailReportItems: (items: FailReportItem[]) => void,
  isEditing: boolean,
) => {
  const { failReportRepository } = useRepositoriesContext();
  // Ref para evitar que refetches posteriores sobreescriban cambios locales no guardados.
  const hasDoneInitialSync = useRef(false);
  const {
    result: failReportItemsResult,
    query: { isLoading, isFetching, refetch, isFetchedAfterMount },
  } = useList<FailReportItem>({
    resource: FAIL_REPORT_URLS.FAIL_REPORTS_ITEMS_RESOURCE,
    pagination: { pageSize: 1000 },
    meta: {
      fail_report: failReportId,
    },
    queryOptions: {
      queryKey: queryKeys.failReport.failReportItems.byFailReport(failReportId),
      enabled: failReportId !== undefined,
      staleTime: 0,
      refetchOnWindowFocus: false,
    },
  });
  const { mutateAsync: saveItems } = useMutation({
    mutationFn: async () => {
      if (failReportId != undefined) {
        await failReportRepository.saveFailReportItems(failReportItems, failReportId);
      }
    },
    onSuccess: async () => {
      const { data } = await refetch();

      if (data) {
        setFailReportItems(data.data);
      }
    },
  });

  useEffect(() => {
    if (isFetchedAfterMount && failReportItemsResult && !isEditing && !hasDoneInitialSync.current) {
      setFailReportItems(failReportItemsResult.data);
      hasDoneInitialSync.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [failReportItemsResult, isFetchedAfterMount]);

  const handleDelete = useCallback(
    record => {
      const itemFounded = failReportItems.find(item =>
        record.id ? item.id == record.id : item.name == record.name,
      );

      if (!itemFounded) return;

      const itemDeleted: FailReportItem = { ...itemFounded, toDelete: true };
      const newFailReportItems = failReportItems.map(item =>
        item.id
          ? item.id === itemDeleted.id
            ? itemDeleted
            : item
          : item.name === itemDeleted.name
            ? itemDeleted
            : item,
      );

      setFailReportItems(newFailReportItems);
    },
    [failReportItems, setFailReportItems],
  );

  return { isLoading, isFetching, refetch, handleDelete, saveItems };
};

export default useFailReportItems;
